import { tmxApiCall } from './index'
import { RegexConstants } from '../constants.js'

// Utilities
import Logger from '../utility/loggerUtil'
import Session from '../utility/sessionUtil'

export default {
  async getLoanSummaries (brandName, maxResultsPerCategory, loanId) {
    Logger.info('Started Get Loan Summaries query.')
    return tmxApiCall('get', '/accounts', {
      headers: {
        'x-mobile-app-id-token': Session.getIdToken()
      },
      params: {
        brandName,
        maxResultsPerCategory,
        loanId
      }
    })
  },

  async getLoanDetail (loanHeader, lmsId, storeId) {
    Logger.info('Started Get Loan Detail query.')
    return tmxApiCall('get', '/accountdetail', {
      headers: {
        'x-mobile-app-id-token': Session.getIdToken()
      },
      params: {
        loanHeader,
        lmsId,
        storeId
      }
    })
  },

  async getAccountHistory (loanHeader, storeId, lmsId) {
    Logger.info('Started Account History query.')
    return tmxApiCall('get', '/accounthistory', {
      params: {
        loanHeader,
        storeId,
        lmsId
      }
    })
  },

  async associate (customerReferralId, dob, ssn, loanId, cognitoUserId) {
    Logger.info('Started associate loan query.')
    const day = dob.getDate()
    const monthIndex = dob.getMonth()
    const year = dob.getFullYear()

    return tmxApiCall('get', '/customer', {
      params: {
        customerReferralId,
        dateOfBirth: year + ('0' + (monthIndex + 1)).slice(-2) + ('0' + day).slice(-2),
        ssn,
        loanId,
        cognitoUserId
      }
    })
  },

  async linkCustomer (username, customerId, lmsId) {
    Logger.info('Started linkCustomer API call.')

    return tmxApiCall('post', '/customer', {
      username,
      customerId,
      lmsId
    })
  },

  async unlinkCustomer (username, lmsId = undefined) {
    Logger.info('Started unlinkCustomer API call.')

    return tmxApiCall('delete', '/customer', {
      data: {
        username,
        lmsId
      }
    })
  },

  /**
   * Gets the current active duty military status of the customer.
   * @param {String} customerId - The customer identifier.
   * @param {String} loanHeader - The loan header identifier.
   * @param {Boolean} migrateProduct - Whether or not the user is migrating products
   * @param {Boolean} isDisbursement - Whether or not the user is disbursing.
   * @param {String} storeId - The store identifier.
   * @param {String|Number} [payDayProductId] - The product identifier returned by the underwriting call, if approved.
   * @param {String} [applicationId] - The application identifier for loan origination.
   * @returns {Promise<{isActiveDuty: Number}>} The customers active duty military status (0 means not active).
   */
  async getMlaStatus (customerId, loanHeader, migrateProduct, isDisbursement, storeId, payDayProductId = undefined, applicationId = undefined) {
    Logger.info('Started MLA Status query.')
    return tmxApiCall('get', '/militarystatus', {
      params: {
        customerId,
        isDisbursement,
        loanHeader,
        migrateProduct,
        storeId,
        payDayProductId,
        applicationId
      }
    })
  },

  async sendPaymentConfirmation (confirmationObj) {
    Logger.info('Started payment confirmation request.')
    return tmxApiCall('post', '/sendConfirmation', {
      profileId: confirmationObj.profileId,
      email: confirmationObj.email,
      phoneNumber: confirmationObj.phoneNumber,
      cardHolderName: confirmationObj.cardHolderName,
      loanId: confirmationObj.loanId,
      loanHeader: confirmationObj.loanHeader,
      confirmationNumber: confirmationObj.confirmationNumber,
      amountPaid: Number.parseFloat(confirmationObj.amountPaid).toFixed(2).replace(RegexConstants.currencyFormatting, '$1,'),
      paymentDate: confirmationObj.paymentDate,
      cardNumber: confirmationObj.cardNumber.substring(confirmationObj.cardNumber.length - 4),
      customerName: confirmationObj.customerName,
      storePhoneNumber: confirmationObj.storePhoneNumber,
      productBrand: confirmationObj.productBrand,
      collateralYear: confirmationObj.collateralYear,
      collateralMake: confirmationObj.collateralMake,
      collateralModel: confirmationObj.collateralModel,
      disbursementFlow: confirmationObj.disbursementFlow,
      disburseBankNumber: confirmationObj.disburseBankNumber,
      disburseCardNumber: confirmationObj.disburseCardNumber,
      isRefi: confirmationObj.isRefi,
      newLoanId: confirmationObj.newLoanId,
      newLoanDueDate: confirmationObj.newLoanDueDate,
      newPrincipalBalance: confirmationObj.newPrincipalBalance,
      messageType: confirmationObj.messageType,
      paymentDueDate: confirmationObj.paymentDueDate,
      isVoidRefi: confirmationObj.isVoidedRefi,
      alternatePhoneNumber: confirmationObj.isVoidedRefi ? undefined : confirmationObj.alternatePhoneNumber,
      alternateEmail: confirmationObj.isVoidedRefi ? undefined : confirmationObj.alternateEmail,
      isPayoff: confirmationObj.isPayoff === true,
      disbursementAmount: confirmationObj.disbursementAmount,
      disbursementType: confirmationObj.disbursementType,
      appointmentDateTime: confirmationObj.appointmentDateTime,
      isPartial: confirmationObj.isPartial === true,
      lmsId: confirmationObj.lmsId,
      migrateProduct: confirmationObj.migrateProduct,
      storeId: confirmationObj.storeId,
      isReapply: confirmationObj.isReapply
    })
  },

  async cancelRenewal (loanHeader, confirmationObj, applicationId) {
    Logger.info('Started cancel renewal post')
    return tmxApiCall('post', '/cancelrefinance', {
      newLoanHeaderId: loanHeader,
      applicationId,
      confirmationData: {
        profileId: confirmationObj.profileId,
        email: confirmationObj.email,
        phoneNumber: confirmationObj.phoneNumber,
        customerId: confirmationObj.customerId,
        cardHolderName: confirmationObj.cardHolderName,
        loanId: confirmationObj.loanId,
        confirmationNumber: confirmationObj.confirmationNumber,
        amountPaid: Number.parseFloat(confirmationObj.amountPaid).toFixed(2).replace(RegexConstants.currencyFormatting, '$1,'),
        paymentDate: confirmationObj.paymentDate,
        cardNumber: confirmationObj.cardNumber.substring(confirmationObj.cardNumber.length - 4),
        customerName: confirmationObj.customerName,
        storePhoneNumber: confirmationObj.storePhoneNumber,
        productBrand: confirmationObj.productBrand,
        collateralYear: confirmationObj.collateralYear,
        collateralMake: confirmationObj.collateralMake,
        collateralModel: confirmationObj.collateralModel,
        paymentDueDate: confirmationObj.paymentDueDate,
        loanSigned: confirmationObj.loanSigned,
        isVoidRefi: true,
        isReapply: confirmationObj.isReapply
      }
    })
  },

  async getCancelRenewalStatus (messageKey) {
    Logger.info('Started Get Cancel Refinance Status query.')
    return tmxApiCall('get', '/cancelrefinance', {
      params: {
        messageKey
      }
    })
  },

  async getCustomerMessages (customerId) {
    Logger.info('Started Get Customer Messages query.')
    return tmxApiCall('get', '/dashboardmessage', {
      params: {
        customerId
      }
    })
  },

  async getNewAccountTerms (loanHeader, loanId, productId, storeId, disbursementAmount, lmsId, guestPayment, migrateProduct, isReapply) {
    Logger.info('Started Account Terms query.')
    return tmxApiCall('get', '/newaccountterms', {
      params: {
        loanHeader,
        loanId,
        productId,
        storeId,
        disbursementAmount,
        lmsId,
        guestPayment,
        migration: migrateProduct,
        isReapply
      }
    })
  },

  /**
   * Gets the loan application status for any loans tied to the user's LMS Customer IDs.
   */
  async getLoanApplicationStatus (maxResults) {
    Logger.info('Started Application Status query.')
    return tmxApiCall('get', '/applicationstatus', {
      headers: {
        'x-mobile-app-id-token': Session.getIdToken()
      },
      params: {
        maxResults
      }
    })
  },

  /**
   * Sends spouse address to service layer.
   * @param {String} customerId - The identifier of the customer that the loan is associated with.
   * @param {Object} address - The address object which should contain, address, city, state, zip.
   * @param {String} firstName - The first name of the spouse.
   * @param {String} lastName - The last name of the spouse.
   * @returns {Promise<{success: Boolean}>}
   */
  async sendSpousalNotification (customerId, address, firstName, lastName) {
    Logger.info('Started Post Spouse Address')
    return tmxApiCall('post', '/spouseAddress', {
      customerId,
      address,
      firstName,
      lastName
    })
  },

  /**
   * Gets the ancillary products for the loan.
   * @param {String} loanHeader - The unique header identifier for the loan.
   * @param {Number} paymentAmount - The amount that the payment is for.
   * @param {Number} productId - The product ID of the loan.
   * @param {Number} storeId - The 4 digit store ID for the loan.
   * @returns {Promise<{products: Array}>} The insurance products associated to the loan.
   */
  async getAncillaryProducts (loanHeader, paymentAmount, productId, storeId) {
    Logger.info('Started Get Ancillary Products')
    return tmxApiCall('get', '/ancillaryProducts', {
      params: {
        loanHeader,
        productId,
        paymentAmount,
        storeId
      }
    })
  },

  /**
   * Draws funds from a line of credit.
   * @param {String} loanHeader - The unique header identifier for the loan.
   * @param {String} loanId - The unique identifier for the loan.
   * @param {Number} storeId - The 4 digit store ID for the loan.
   * @param {Number} disbursementAmount - The amount to be drawn from the loan.
   * @param {Object} [disburseCardIds] - The token representing the disbursement debit card to push funds to. Only used for push to debit disbursements.
   * @param {Number} disbursementType - A single digit number, defined by DisbursementTypeEnum in constants.js.
   * @param {String} lmsId - The identifier for the LMS that the loan is associated with.
   * @param {String} username - The email or phone number the customer is signed in with.
   * @param {String} guestPayment - If the user did the draw from a guest link or not.
   * @returns {Promise<{confirmationNumber: String, newLoanId: String, newPrincipalBalance: Number, newLoanDueDate: String}>}
   */
  async draw (loanHeader, loanId, storeId, disbursementAmount, disburseCardIds, disbursementType, lmsId, username, guestPayment) {
    Logger.info('Started draw for disbursement')
    return tmxApiCall('post', '/draw', {
      loanHeader,
      loanId,
      storeId,
      disbursementAmount,
      disburseCardIds,
      disbursementType,
      lmsId,
      username,
      guestPayment
    })
  },

  /**
   * Gets the post draw response from /draw.
   * @param {String} messageKey - The key associated with the draw.
   * @returns {Promise<{success: Boolean, Error: String, confirmationNumber: String, newLoanId: String, newLoanDueDate: String, newPrincipalBalance: Number}>} Payment result information.
   */
  async getDrawStatus (messageKey) {
    Logger.info('Started Check Post Draw status query.')
    return tmxApiCall('get', '/draw', {
      params: {
        messageKey
      }
    })
  },

  /**
   * Gets repo freeze status based on the loan's collateral VIN.
   * @param {String} loanHeader - The unique header identifier for the loan.
   * @param {String} storeId - The store identifier.
   * @returns {Promise<{frozen: Boolean}>}
   */
  async getRepoInfo (loanHeader, storeId) {
    Logger.info('Started get repo info')
    return tmxApiCall('get', '/repoInfo', {
      params: {
        loanHeader,
        storeId
      }
    })
  },

  /**
   * Sets the repo freeze status on the collateral associated with the passed in loan.
   * @param {String} loanHeader - The unique header identifier for the loan.
   * @param {String} storeId - The store identifier.
   */
  async setRepoFreeze (loanHeader, storeId) {
    Logger.info('Started set repo freeze')
    return tmxApiCall('put', '/repoInfo', {
      loanHeader,
      storeId
    })
  },

  /**
   * Gets details on which types of risk details are applicable for the customer and loan.
   * @param {Boolean} isAddon - True if the refinance is part of an addon or not.
   * @param {String} loanId - The unique identifier for the loan.
   * @param {String} migrateProduct - True if a product migration was chosen.
   * @returns {Promise<{status: {rateChanged: Boolean, creditLimitChanged: Boolean, refinanceDenied: Boolean}}>} The risk details for the customer and loan.
   */
  async getRiskInfo (isAddon, loanId, migrateProduct) {
    Logger.info('Started get risk info.')
    return tmxApiCall('get', '/riskInfo', {
      params: {
        isAddon,
        loanId,
        migrateProduct
      }
    })
  }
}
